import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  LinearProgress,
  List,
  ListItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format, parseISO, isBefore } from 'date-fns';
// Removed duplicate imports - using the ones from above

interface ProductionStage {
  id: string;
  name: string;
  description: string;
  stage_number: number;
  start_date: string | null;
  end_date: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'delayed';
  notes: string | null;
  proof_required: boolean;
  proof_submitted: boolean;
  proof_approved: boolean;
  proof_notes: string | null;
}

interface ProductionPlan {
  id: string;
  plan_name: string;
  production_type: 'crop' | 'animal';
  variety_id: string;
  variety_name: string;
  variety_scientific_name: string | null;
  farmer_id: string;
  farmer_name: string;
  advisor_id: string;
  advisor_name: string;
  planned_start_date: string;
  planned_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  target_area_hectares: number | null;
  target_animal_count: number | null;
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'delayed';
  progress_percentage: number;
  description: string;
  created_at: string;
  updated_at: string;
  stages: ProductionStage[];
}

const ProductionPlanView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<ProductionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch production plan details
  const fetchProductionPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/production-plans/${id}`);
      setPlan(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching production plan:', err);
      setError('Failed to load production plan. Please try again later.');
      toast.error('Failed to load production plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProductionPlan();
    }
  }, [id]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  // Handle actual deletion
  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/production-plans/${id}`);
      toast.success('Production plan deleted successfully');
      navigate('/production-plans');
    } catch (err) {
      console.error('Error deleting production plan:', err);
      toast.error('Failed to delete production plan');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not started';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'completed':
        return 'success';
      case 'delayed':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'draft':
      default:
        return 'default';
    }
  };

  // Get stage status
  const getStageStatus = (stage: ProductionStage) => {
    if (stage.status === 'completed') return 'Completed';
    if (stage.status === 'in_progress') return 'In Progress';
    if (stage.status === 'delayed') return 'Delayed';
    
    // Check if stage is pending but past its planned date
    if (stage.start_date && isBefore(parseISO(stage.start_date), new Date())) {
      return 'Delayed';
    }
    
    return 'Pending';
  };

  // Get stage status color
  const getStageStatusColor = (stage: ProductionStage) => {
    if (stage.status === 'completed') return 'success';
    if (stage.status === 'in_progress') return 'primary';
    if (stage.status === 'delayed') return 'error';
    
    // Check if stage is pending but past its planned date
    if (stage.start_date && isBefore(parseISO(stage.start_date), new Date())) {
      return 'error';
    }
    
    return 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !plan) {
    return (
      <Box p={3}>
        <Typography color="error">{error || 'Production plan not found'}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/production-plans')}
          sx={{ mt: 2 }}
        >
          Back to Production Plans
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/production-plans')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {plan.plan_name}
        </Typography>
        <Box flexGrow={1} />
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
          sx={{ mr: 1 }}
        >
          Delete
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          component={Link}
          to={`/production-plans/edit/${plan.id}`}
        >
          Edit
        </Button>
      </Box>

      {/* Status and Progress */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center">
            <Chip
              label={plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
              color={getStatusColor(plan.status) as any}
              size="small"
              sx={{ textTransform: 'capitalize', mr: 1 }}
            />
            <Typography variant="body2" color="textSecondary">
              {plan.progress_percentage.toFixed(0)}% complete
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Created on {format(new Date(plan.created_at), 'MMM d, yyyy')}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={plan.progress_percentage}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="production plan tabs"
        >
          <Tab label="Overview" />
          <Tab label="Stages" />
          <Tab label="Resources" />
          <Tab label="Activity" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Plan Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Production Type
                        </Typography>
                        <Typography>
                          {plan.production_type === 'crop' ? 'Crop' : 'Animal'} Production
                        </Typography>
                      </Box>
                      
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {plan.production_type === 'crop' ? 'Crop Variety' : 'Animal Breed'}
                        </Typography>
                        <Typography>
                          {plan.variety_name}
                          {plan.variety_scientific_name && (
                            <Typography variant="body2" color="textSecondary">
                              {plan.variety_scientific_name}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Description
                        </Typography>
                        <Typography>
                          {plan.description || 'No description provided.'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Planned Timeline
                        </Typography>
                        <Box display="flex" alignItems="center" mb={1}>
                          <span>📅</span>
                          <Typography variant="body2" ml={1}>
                            {format(new Date(plan.planned_start_date), 'MMM d, yyyy')} - {plan.planned_end_date ? format(new Date(plan.planned_end_date), 'MMM d, yyyy') : 'Ongoing'}
                          </Typography>
                        </Box>
                        
                        {plan.actual_start_date && (
                          <Box display="flex" alignItems="center">
                            <span>📆</span>
                            <Typography variant="body2" ml={1}>
                              <strong>Actual:</strong> {format(new Date(plan.actual_start_date), 'MMM d, yyyy')}
                              {plan.actual_end_date ? ` - ${format(new Date(plan.actual_end_date), 'MMM d, yyyy')}` : ' (In Progress)'}
                              <strong>Actual:</strong> {formatDate(plan.actual_start_date)}
                              {plan.actual_end_date ? ` - ${formatDate(plan.actual_end_date)}` : ' (In Progress)'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {plan.production_type === 'crop' ? 'Target Area' : 'Target Count'}
                        </Typography>
                        <Typography>
                          {plan.production_type === 'crop'
                            ? `${plan.target_area_hectares || 0} hectares`
                            : `${plan.target_animal_count || 0} animals`}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                          Advisor
                        </Typography>
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: '8px' }}>👤</span>
                          <Typography>{plan.advisor_name || 'Not assigned'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Recent Activity */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Activity
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography color="textSecondary">
                    No recent activity to display.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Right Column */}
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Farmer
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" alignItems="center" mb={2}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#1976d2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      👤
                    </div>
                    <Box>
                      <Typography variant="subtitle1">{plan.farmer_name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Farmer
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    component={Link}
                    to={`/farmers/${plan.farmer_id}`}
                  >
                    View Farmer Profile
                  </Button>
                </CardContent>
              </Card>
              
              {/* Key Dates */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Key Dates
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    <ListItem>
                      <div style={{ minWidth: '40px', textAlign: 'center' }}>📅</div>
                      <div>
                        <div>Start Date</div>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                          {formatDate(plan.planned_start_date)}
                        </div>
                      </div>
                    </ListItem>
                    <ListItem>
                      <div style={{ minWidth: '40px', textAlign: 'center' }}>⏱️</div>
                      <div>
                        <div>End Date</div>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                          {plan.planned_end_date ? formatDate(plan.planned_end_date) : 'Not set'}
                        </div>
                      </div>
                    </ListItem>
                    {plan.actual_start_date && (
                      <ListItem>
                        <div style={{ minWidth: '40px', textAlign: 'center' }}>✅</div>
                        <div>
                          <div>Started</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            {format(new Date(plan.actual_start_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      </ListItem>
                    )}
                    {plan.actual_end_date && (
                      <ListItem>
                        <div style={{ minWidth: '40px', textAlign: 'center' }}>🏁</div>
                        <div>
                          <div>Completed</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            {format(new Date(plan.actual_end_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Production Stages
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {plan.stages && plan.stages.length > 0 ? (
                <Box>
                  {plan.stages.map((stage) => (
                    <Paper key={stage.id} sx={{ p: 2, mb: 2, borderLeft: `4px solid ${getStageStatusColor(stage) === 'success' ? '#4caf50' : getStageStatusColor(stage) === 'error' ? '#f44336' : getStageStatusColor(stage) === 'primary' ? '#1976d2' : '#9e9e9e'}` }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {stage.name}
                          </Typography>
                          <Chip
                            label={getStageStatus(stage)}
                            size="small"
                            color={getStageStatusColor(stage) as any}
                            sx={{ mt: 0.5, mb: 1 }}
                          />
                          <Typography variant="body2" color="textSecondary">
                            {stage.description}
                          </Typography>
                          
                          <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                Planned
                              </Typography>
                              <Typography variant="body2">
                                {stage.start_date ? formatDate(stage.start_date) : 'Not set'}
                                {stage.end_date ? ` - ${formatDate(stage.end_date)}` : ''}
                              </Typography>
                            </Box>
                            
                            {stage.notes && (
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  Notes
                                </Typography>
                                <Typography variant="body2">
                                  {stage.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          {stage.proof_required && (
                            <Box mt={1}>
                              <Typography variant="caption" color="textSecondary">
                                Proof Required: {stage.proof_submitted ? 'Submitted' : 'Pending'}
                                {stage.proof_approved !== null && (
                                  <Chip
                                    label={stage.proof_approved ? 'Approved' : 'Rejected'}
                                    size="small"
                                    color={stage.proof_approved ? 'success' : 'error'}
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                              {stage.proof_notes && (
                                <Typography variant="body2" color="textSecondary">
                                  {stage.proof_notes}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                        
                        <Box>
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <InfoIcon color="info" sx={{ color: 'info.main' }} fontSize="large" />
                  <Typography color="textSecondary" gutterBottom>
                    No stages found for this production plan.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(`/production-plans/edit/${plan.id}`)}
                  >
                    Add Stages
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        
        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resources
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Typography color="textSecondary">
                No resources available for this production plan.
              </Typography>
            </CardContent>
          </Card>
        )}
        
        {activeTab === 3 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity Log
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Typography color="textSecondary">
                No activity to display.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Production Plan
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the production plan "{plan.plan_name}"?
            This action cannot be undone and all associated data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionPlanView;
